import { dialogsAtom, popDialog, replaceDialog, resetDialogs } from "@/store/dialogs-state"
import { store } from "@/store/store"
import { CatalogManager, PhotoRecord } from "@/lib/catalog"
import {
	AdjustmentsHistoryRecord,
	HistoryRecordAction,
	PhotoMetadataRecord,
} from "@/lib/catalog/types"
import {
	encodeImageData,
	exportImageData,
	ExportImageOptions,
	FilenameType,
	getExportImageSize,
	getYUVExportByteLength,
} from "@/core/export-image"
import {
	ExportExistingFilesDialog,
	ExportExistingFilesResult,
	ExportLoadingDialog,
} from "@/components/Dialogs/ExportDialog"
import { LoadingDialog } from "@/components/Dialogs/LoadingDialog"
import { atomWithReset, RESET } from "jotai/utils"
import { currentPhotoAtom, getPhotoMetadata, metadataAtom } from "@/store/photo/photos-state"
import { formatISODate } from "@/utils/date"
import PQueue from "p-queue"
import { getEditor } from "@/utils/useEditor"
import { getFileForPhoto } from "@/store/files-state"
import { Sentry } from "@/telemetry/sentry"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"
import { buildExportCreditConsumptionEvent, consumeExportCredits } from "@/api/payments-api"
import toast from "react-hot-toast"
import {
	defaultMaskAdjustments,
	getDefaultAdjustments,
	getEmbeddableAdjustments,
} from "@/core/adjustments"
import { isRealColorTag } from "@/utils/exif"
import { analytics } from "@/telemetry/amplitude"
import { ProcessingPerformance } from "@/telemetry/processing-performance"
import { currentStyleAtom, showAddEditsToStyleDialog } from "../style/style-state"
import { CreditsConsumptionMetadata } from "@/api/dto/payment"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import {
	buildExportDetails,
	getExportDetailsSummary,
	shouldShowExportDetails,
	type ExportDetails,
} from "@/components/export-details/export-details-utils"
import { ExportDetailsView } from "@/components/export-details/ExportDetailsView"
import {
	beginTaskPerformanceMeasurement,
	commitTaskPerformanceMeasurement,
} from "../debug/task-performance-state"
import { JpegEncoder, type JpegYUVBufferLease } from "@/lib/jpeg/jpeg-encoder"
import { getImageInfo } from "../processing/image-info"
import { PixelData } from "@/core/utils/image-data"
import { Adjustments } from "@/core/types"
import { atom } from "jotai"
import { getExportCompleteEventProperties, getIsAIEditOnlyFromHistory } from "./export-event-utils"
import { getExportCreditConsumptionFailureMessage } from "./export-credit-consumption-utils"
import { isManualModeAtom, projectAtom } from "../project/projects-state"
import {
	commitTrackingPerformanceValue,
	startTrackingPerformanceValue,
} from "@/utils/performance-stats"
import {
	handlePhotoExportError,
	showPhotoExportStorageErrorDialog,
	type PhotoExportStorageError,
} from "./photo-export-error"
import {
	isValidatedExportType,
	validateEncodedExportSizeAndReport,
} from "@/core/utils/encoded-size-check"
import { holdDisplayWakeLock } from "@/utils/display-wake-lock"
import { subscribeSystemSleepEvents } from "@/utils/system-sleep-events"
import { insertWordInFilename } from "./filename-utils"
import { photoExportingSession, type PhotoExportingSession } from "./photo-exporting-session"
import { isAbortError } from "@/utils/error"
import { createElement } from "react"
import { isBrowserOffline } from "@/api/network-behavior"
import {
	cleanupPhotoOutputWorkers,
	preparePhotoOutputWorkers,
} from "./photo-output-worker-lifecycle"
import { TiffEncoder, type TiffRGBDataBufferLease } from "@/lib/tiff/tiff-encoder"
import { getTIFFBufferByteLengths, type EncodedTIFFData } from "@/core/encoders/tiff-encoder"
import { PSDEncoder, type PSDRGBDataBufferLease } from "@/lib/psd/psd-encoder"
import { getPSDBufferByteLengths, type EncodedPSDData } from "@/core/encoders/psd-encoder"
import type { PreparedIOAdapterImport } from "@/core/io-adapter"
import { PhotoOutputLookahead, shouldPrefetchPhotoOutputImport } from "./photo-output-lookahead"

export const cancelExportAtom = atomWithReset(false)
const EXPORT_CANCELING_DIALOG_TITLE = "Cancelling export..." // i18n-ignore — used as dialog identity check
const RGB_PLANAR_EXPORT_BUFFER_HEADROOM = 1.15
type ExportCancelReason = "user-canceled" | "storage-error" | "credit-consumption-failure"
type RGBPlanarDataBufferLease = TiffRGBDataBufferLease | PSDRGBDataBufferLease
let pendingExportCancelReason: ExportCancelReason | undefined
let pendingExportCreditFailureMessage: string | undefined
let pendingExportStorageError: PhotoExportStorageError | undefined

const totalToExportAtom = atomWithReset(0)
const photosExportedAtom = atomWithReset<string[]>([])
const photosSavedAtom = atomWithReset<Set<string>>(new Set())
const photosFailedAtom = atomWithReset<string[]>([])

export const exportProgressAtom = atom((get) => {
	const total = get(totalToExportAtom)
	const exports = get(photosExportedAtom)
	const saves = get(photosSavedAtom)
	const failed = get(photosFailedAtom)

	return {
		exports: exports.length,
		saves: saves.size,
		totalToSave: total,
		total: total * 2,
		totalProgress: exports.length + saves.size + failed.length,
	}
})

export const cancelExport = (dismissDialog: boolean = true) => {
	if (dismissDialog) {
		popDialog()
	}
	store.set(cancelExportAtom, true)
	photoExportingSession.cancelCurrent("export")
}

function reset() {
	store.set(cancelExportAtom, RESET)
	store.set(photosExportedAtom, RESET)
	store.set(photosSavedAtom, RESET)
	store.set(photosFailedAtom, RESET)
	store.set(totalToExportAtom, RESET)
}
function showExportCancelingDialog() {
	replaceDialog(LoadingDialog, { title: EXPORT_CANCELING_DIALOG_TITLE })
}

export function cancelExportFromDialog() {
	pendingExportCancelReason = "user-canceled"
	store.set(cancelExportAtom, true)
	showExportCancelingDialog()
	photoExportingSession.cancelCurrent("export")
}

function popExportSessionDialogIfActive() {
	const [component, props] = store.get(dialogsAtom).at(-1) ?? []
	if (
		component === ExportLoadingDialog ||
		(component === LoadingDialog &&
			(props as { title?: string } | undefined)?.title === EXPORT_CANCELING_DIALOG_TITLE)
	) {
		popDialog()
	}
}

export async function exportPhotos(
	photos: PhotoRecord[],
	directory: FileSystemDirectoryHandle,
	options: ExportImageOptions,
	filenameOverrides?: Record<string, string>,
) {
	if (!photos.length) {
		return
	}
	exportDirectory = directory

	const session = await photoExportingSession.beginSession("export")
	const performance = new ProcessingPerformance("export", {
		projectId: options.projectId,
		styleId: store.get(currentStyleAtom)?.styleId,
		outputFormat: options.type,
		destination: "local",
	})
	pendingExportCancelReason = undefined
	pendingExportCreditFailureMessage = undefined
	pendingExportStorageError = undefined
	let showPostCleanupDialog: (() => void) | undefined
	let filenames: Record<string, string> = {}
	let total = photos.length
	let photosToExport = photos
	const aiEditOnlyByPhotoId = new Map<string, boolean>()
	let releaseExportWakeLock: (() => void) | undefined
	let unsubscribeSleepEvents: (() => void) | undefined

	const getAnalyticsInfo = (extras = {}) => {
		const info = {
			projectId: options.projectId,
			styleId: store.get(currentStyleAtom)?.styleId,
			totalCount: total,
		}

		return { ...info, ...extras }
	}

	const getExportDetails = (options?: { failedFilenames?: string[] }): ExportDetails => {
		return buildExportDetails({
			flow: "export",
			completedFilenames: Array.from(store.get(photosSavedAtom)),
			allFilenames: Object.values(filenames),
			failedFilenames: options?.failedFilenames,
		})
	}

	const getPhotosForFilenames = (targetFilenames: string[]) => {
		const targetFilenameSet = new Set(targetFilenames)
		return photosToExport.filter((photo) => {
			const filename = filenames[photo.id]
			return filename ? targetFilenameSet.has(filename) : false
		})
	}

	const hasFinishedWritingAllExportResults = () => {
		const successCount = store.get(photosSavedAtom).size
		const failedCount = store.get(photosFailedAtom).length
		return successCount + failedCount >= total
	}

	const retryFailedExports = (details: ExportDetails) => {
		if (isBrowserOffline()) {
			replaceDialog(ConfirmDialog, {
				title: i18n.t("store:photoExporting.exportFailedTitle"),
				message: i18n.t("store:photoExporting.exportFailedOfflineMessage", {
					details: getExportDetailsSummary(details),
				}),
				primary: {
					title: i18n.t("common:action.retry"),
					handler: () => retryFailedExports(details),
				},
				secondary: {
					title: i18n.t("common:action.ok"),
				},
				embeddedComponent: shouldShowExportDetails(details)
					? createElement(ExportDetailsView, { details })
					: undefined,
				trackingViewName: "export_retry_offline_dialog",
			})
			return
		}

		const retryPhotos = getPhotosForFilenames(details.failedFilenames)

		if (retryPhotos.length === 0) {
			return
		}

		const retryFilenames = Object.fromEntries(
			retryPhotos.flatMap((photo) => {
				const filename = filenames[photo.id]
				return filename ? [[photo.id, filename]] : []
			}),
		)

		void exportPhotos(retryPhotos, directory, options, retryFilenames)
	}

	const showCanceledExportDialog = (details: ExportDetails) => {
		replaceDialog(ConfirmDialog, {
			title: i18n.t("store:photoExporting.exportCanceledTitle"),
			message: getExportDetailsSummary(details), // i18n-ignore — dynamic summary from export-details-utils
			primary: {
				title: i18n.t("common:action.ok"),
				handler: () => {
					resetDialogs()
				},
			},
			embeddedComponent: shouldShowExportDetails(details)
				? createElement(ExportDetailsView, { details })
				: undefined,
			trackingViewName: "export_canceled_dialog",
		})
	}

	const showExportCreditConsumptionFailureDialog = (
		failureMessage: string,
		details: ExportDetails,
	) => {
		const retryHandler =
			details.failedFilenames.length > 0 ? () => retryFailedExports(details) : undefined

		replaceDialog(ConfirmDialog, {
			title: i18n.t("store:photoExporting.exportIncompleteTitle"),
			message: `${failureMessage} ${getExportDetailsSummary(details)}`, // i18n-ignore — failureMessage is already from server
			primary: {
				title: retryHandler ? i18n.t("common:action.retry") : i18n.t("common:action.ok"),
				handler: retryHandler
					? retryHandler
					: () => {
							resetDialogs()
						},
			},
			secondary: retryHandler
				? {
						title: i18n.t("common:action.ok"),
						handler: () => {
							resetDialogs()
						},
					}
				: undefined,
			embeddedComponent: shouldShowExportDetails(details)
				? createElement(ExportDetailsView, { details })
				: undefined,
			trackingViewName: "export_credit_consumption_failed_dialog",
		})
	}

	const handleCreditConsumptionFailure = (failureMessage: string) => {
		if (!session.isCurrent() || session.isCanceled || hasFinishedWritingAllExportResults()) {
			return
		}

		pendingExportCancelReason = "credit-consumption-failure"
		pendingExportCreditFailureMessage = failureMessage
		cancelExport(false)
	}

	const handleCompletionIfNeeded = () => {
		if (!session.isCurrent() || session.isCanceled) {
			return
		}

		const successCount = store.get(photosSavedAtom).size
		const failedExports = store.get(photosFailedAtom)
		const isFinished = successCount + failedExports.length === total
		const isManualMode = store.get(isManualModeAtom)

		if (isFinished) {
			const details = getExportDetails({ failedFilenames: failedExports })
			const retryHandler =
				details.failedFilenames.length > 0 ? () => retryFailedExports(details) : undefined
			let message = ""
			if (successCount > 0) {
				message += `Exported ${
					successCount > 1 ? `${successCount} photos` : "a photo"
				} successfully.`
			}
			if (failedExports.length > 0) {
				message += ` Failed to export ${
					failedExports.length > 1 ? `${failedExports.length} photos` : "a photo"
				}.`
			}

			replaceDialog(ConfirmDialog, {
				title: i18n.t("store:photoExporting.exportCompleteTitle"),
				message,
				primary: {
					title: retryHandler ? "Retry" : "OK",
					handler: retryHandler
						? retryHandler
						: () => {
								resetDialogs()
							},
				},
				secondary: retryHandler
					? {
							title: i18n.t("common:action.ok"),
							handler: () => {
								resetDialogs()
							},
						}
					: isManualMode || successCount === 0
						? undefined
						: {
								title: i18n.t("store:photoExporting.trainMyAiStyle"),
								handler: () => {
									showAddEditsToStyleDialog()
								},
							},
				embeddedComponent: shouldShowExportDetails(details)
					? createElement(ExportDetailsView, { details })
					: undefined,
				trackingViewName: "export_complete_dialog",
			})

			if (successCount === total) {
				const aiEditOnlyCount = photosToExport.filter(
					(photo) => aiEditOnlyByPhotoId.get(photo.id) === true,
				).length

				analytics.trackEvent({
					label: "export_complete",
					type: "event",
					customProperties: getExportCompleteEventProperties({
						destination: "local",
						projectId: options.projectId,
						exportedPhotos: photosToExport,
						completedCount: successCount,
						aiEditOnlyCount,
					}),
				})
			}

			//delay resetting the state to allow completion dialog to display first
			setTimeout(() => {
				reset()
			}, 500)
		}
	}

	try {
		reset()
		store.set(totalToExportAtom, total)
		replaceDialog(ExportLoadingDialog)

		filenames = filenameOverrides ?? (await getPhotoFilenames(photos, options))
		session.throwIfCanceled()

		total = Object.keys(filenames).length
		photosToExport = photos.filter((photo) => Boolean(filenames[photo.id]))
		store.set(totalToExportAtom, total)

		if (!filenames || total === 0) {
			reset()
			popExportSessionDialogIfActive()
			return
		}

		replaceDialog(ExportLoadingDialog)

		const queues = await batchExportPhotos({
			session,
			performance,
			photos,
			options,
			filenames,
			onCreditConsumptionFailure: handleCreditConsumptionFailure,
			onHistoryRecordLoaded: (photo, historyRecord) => {
				if (session.isCurrent() && !session.isCanceled) {
					aiEditOnlyByPhotoId.set(photo.id, getIsAIEditOnlyFromHistory(historyRecord))
				}
			},
			errorHandler: (photoId, filename, error) => {
				if (!session.isCurrent() || session.isCanceled) {
					return
				}
				performance.failed()
				handlePhotoExportError({
					error,
					photoId,
					flow: "export",
					onStorageError: (storageError) => {
						pendingExportCancelReason = "storage-error"
						pendingExportStorageError = storageError
						cancelExport(false)
					},
				})

				store.set(photosFailedAtom, (prev) => [...prev, filename])
			},
			successHandler: (filename) => {
				if (!session.isCurrent() || session.isCanceled) {
					return
				}
				store.set(photosSavedAtom, (prev) => new Set(prev).add(filename))
			},
		})

		if (!queues) return

		const { queue, encodeQueue, fileQueue } = queues

		session.trackQueue(queue)
		session.trackQueue(encodeQueue)

		if (total === 1) {
			queue.on("error", () => {
				if (!session.isCanceled) {
					toast.error(
						i18n.t("store:photoExporting.failedExportPhoto", {
							filename: Object.values(filenames)[0],
						}),
					)
				}
			})
		}

		await preparePhotoOutputWorkers("export", session)

		beginTaskPerformanceMeasurement("export")

		unsubscribeSleepEvents = subscribeSystemSleepEvents({
			onPause: () => {
				if (!store.get(cancelExportAtom)) {
					console.log("[SleepResume][Export] Pausing export queue due to sleep")
					queue.pause()
				}
			},
			onResume: () => {
				if (!store.get(cancelExportAtom)) {
					console.log("[SleepResume][Export] Resuming export queue after wake")
					queue.start()
				}
			},
		})

		console.log("[SleepResume][Export] Acquiring wake lock")
		releaseExportWakeLock = holdDisplayWakeLock()
		queue.start()
		analytics.trackEvent({
			label: "export_start",
			type: "event",
			customProperties: getAnalyticsInfo(false),
		})

		await queue.onIdle()
		await encodeQueue.onIdle()
		await fileQueue.onIdle()
		performance.finish(session.isCanceled ? "canceled" : "completed")

		if (!session.isCanceled) {
			commitTaskPerformanceMeasurement("export")
			await reloadCurrentPhotoIntoEditorForExport(session)
			handleCompletionIfNeeded()
		}
	} finally {
		performance.finish(session.isCanceled ? "canceled" : "failed")
		await session.releaseAllLeases()
		if (releaseExportWakeLock) {
			console.log("[SleepResume][Export] Releasing wake lock")
			releaseExportWakeLock()
			releaseExportWakeLock = undefined
		}
		if (unsubscribeSleepEvents) {
			console.log("[SleepResume][Export] Unsubscribing sleep event listeners")
			unsubscribeSleepEvents()
			unsubscribeSleepEvents = undefined
		}
		TiffEncoder.releaseBuffers()
		PSDEncoder.releaseBuffers()

		if (session.isCanceled) {
			const canceledDetails = getExportDetails()
			const cancelReason = pendingExportCancelReason
			const creditFailureMessage = pendingExportCreditFailureMessage
			const storageError = pendingExportStorageError
			await reloadCurrentPhotoIntoEditorForExport(session, { allowCanceled: true })
			reset()
			if (cancelReason === "user-canceled") {
				showPostCleanupDialog = () => showCanceledExportDialog(canceledDetails)
			} else if (cancelReason === "storage-error" && storageError) {
				const retryHandler =
					canceledDetails.failedFilenames.length > 0
						? () => retryFailedExports(canceledDetails)
						: undefined
				showPostCleanupDialog = () =>
					showPhotoExportStorageErrorDialog({
						flow: "export",
						storageError,
						storageErrorDetails: canceledDetails,
						retryHandler,
					})
			} else if (cancelReason === "credit-consumption-failure" && creditFailureMessage) {
				showPostCleanupDialog = () =>
					showExportCreditConsumptionFailureDialog(creditFailureMessage, canceledDetails)
			}
		}

		cleanupPhotoOutputWorkers()
		photoExportingSession.finishSession(session)
		if (session.isCanceled) {
			popExportSessionDialogIfActive()
		}
		pendingExportCancelReason = undefined
		pendingExportCreditFailureMessage = undefined
		pendingExportStorageError = undefined
		showPostCleanupDialog?.()
	}
}

async function getPhotoFilenames(photos: PhotoRecord[], options: ExportImageOptions) {
	if (!exportDirectory) return {}
	const filenames: Record<string, string> = {}
	const duplicatesNames: Record<string, any> = {}
	const duplicatesExist: Record<string, string> = {}

	const checkDuplicates = async (photoId: string, name: string) => {
		try {
			if (exportDirectory) {
				const hasDuplicate = await exportDirectory.getFileHandle(name, { create: false })
				if (hasDuplicate) {
					duplicatesExist[photoId] = name
				}
			}
		} catch (e) {
			// no duplicate
		}
	}

	for (let index = 0; index < photos.length; index++) {
		const photo = photos[index]
		if (!photo) continue

		let name = getExportedFilename({
			photo,
			...options,
			number: options.filenameNumber ? options.filenameNumber + index : index,
			fileExtension: options.type,
		})

		if (name) {
			if (duplicatesNames[name] === undefined) {
				// no duplicate yet
				duplicatesNames[name] = photo.id
			} else if (typeof duplicatesNames[name] === "string") {
				// first time duplicate, modify the previous one's filename
				const duplicatePhotoId = duplicatesNames[name]

				// possible duplicate file in storage
				delete duplicatesExist[duplicatePhotoId]
				duplicatesNames[name] = 1
				const newName = getNewFilenameWithExtension(name, 1)
				filenames[duplicatePhotoId] = newName
				await checkDuplicates(duplicatePhotoId, newName)

				// modify the current filename
				duplicatesNames[name] += 1
				name = getNewFilenameWithExtension(name, duplicatesNames[name])
			} else {
				// already has a duplicate and addressed
				duplicatesNames[name] += 1
				name = getNewFilenameWithExtension(name, duplicatesNames[name])
			}

			filenames[photo.id] = name
			await checkDuplicates(photo.id, name)
		}
	}

	if (Object.keys(duplicatesExist).length > 0) {
		const result = await confirmOverwriteDuplicates(Object.values(duplicatesExist))
		if (result === "cancel") {
			return {}
		} else if (result === "skip") {
			Object.keys(duplicatesExist).forEach((id) => delete filenames[id])
			return filenames
		}
	}
	return filenames
}

async function confirmOverwriteDuplicates(
	existingFiles: string[],
): Promise<ExportExistingFilesResult> {
	return new Promise((resolve) => {
		replaceDialog(ExportExistingFilesDialog, {
			existingFiles,
			resultHandler: (result) => {
				resolve(result)
				resetDialogs()
			},
		})
	})
}

let exportDirectory: FileSystemDirectoryHandle | null = null

interface LoadPhotoForExportOptions {
	photoId?: string
	skipHistogram?: boolean
	skipRenderOnLoad?: boolean
}

export interface PreparedPhotoForExport {
	preparedImport: PreparedIOAdapterImport
	adjustments: Adjustments
	historyRecord: AdjustmentsHistoryRecord | undefined
	metadata: PhotoMetadataRecord | undefined
	dispose(): Promise<void>
}

// Resolves production export state and starts adapter preparation without touching the renderer.
export async function preparePhotoForExport(
	photo: PhotoRecord,
	signal?: AbortSignal,
	options?: LoadPhotoForExportOptions,
): Promise<PreparedPhotoForExport> {
	const editor = getEditor()
	const file = getFileForPhoto(photo)

	if (!file) {
		throw new Error("[Export] File not found")
	}

	signal?.throwIfAborted()

	// TODO: restore measurement
	//const measurementTask = beginTaskSingleItemPerformanceMeasurement("export")
	//commitTaskSingleItemPerformanceMeasurement(measurementTask)

	const defaultAdjustments = getDefaultAdjustments(photo)

	const historyRecord = await CatalogManager.fetchAdjustmentHistoryRecord(
		photo.id,
		photo?.historyIndex ?? 0,
	)

	signal?.throwIfAborted()

	if (!historyRecord || !historyRecord.adjustments) {
		Sentry.captureMessage("Exporting photo with incorrect adjustments", {
			extra: { historyRecord, photoRecord: photo },
		})
	}

	// merge history adjustments with default adjustments for backward compatibility
	const adjustments = {
		...defaultAdjustments,
		...historyRecord?.adjustments,
	}

	// backward compatibility for local adjustments (for any newly added param)
	adjustments.localAdjustments = adjustments.localAdjustments?.map((maskGroup) => ({
		...defaultMaskAdjustments,
		...maskGroup,
	}))

	const metadata = getPhotoMetadata(photo.id)

	/* Source preparation */

	const adapter = editor.ioAdapters.find((candidate) => candidate.canLoad(file))
	if (!adapter) {
		throw new Error(`[Export] No matching IO adapter found for ${file.name}`)
	}

	const preparedImport = await adapter.prepare(file, {
		photoId: photo.id,
		skipRenderOnLoad: options?.skipRenderOnLoad ?? true,
		skipHistogram: options?.skipHistogram ?? true,
		adjustments,
		enableDenoise: true,
		enableHalfResolution: false,
		metadata,
		signal,
	})

	try {
		signal?.throwIfAborted()
	} catch (error) {
		await preparedImport.dispose()
		throw error
	}

	return {
		preparedImport,
		adjustments,
		metadata,
		historyRecord,
		dispose: () => preparedImport.dispose(),
	}
}

export async function loadPreparedPhotoIntoEditorForExport(
	preparedPhoto: PreparedPhotoForExport,
): Promise<{
	adjustments: Adjustments
	historyRecord: AdjustmentsHistoryRecord | undefined
	metadata: PhotoMetadataRecord | undefined
}> {
	const { preparedImport, adjustments, metadata, historyRecord } = preparedPhoto
	preparedImport.options.signal?.throwIfAborted()

	// TODO: optimization: skip importing if photo is already loaded in the editor
	await getEditor().import(preparedImport.file, preparedImport.options)

	preparedImport.options.signal?.throwIfAborted()
	return { adjustments, metadata, historyRecord }
}

export async function loadPhotoIntoEditorForExport(
	photo: PhotoRecord,
	signal?: AbortSignal,
	options?: LoadPhotoForExportOptions,
): Promise<{
	adjustments: Adjustments
	historyRecord: AdjustmentsHistoryRecord | undefined
	metadata: PhotoMetadataRecord | undefined
}> {
	const preparedPhoto = await preparePhotoForExport(photo, signal, options)
	try {
		return await loadPreparedPhotoIntoEditorForExport(preparedPhoto)
	} finally {
		await preparedPhoto.dispose()
	}
}

export async function reloadCurrentPhotoIntoEditorForExport(
	session: PhotoExportingSession,
	options?: { allowCanceled?: boolean },
) {
	const currentPhoto = store.get(currentPhotoAtom)
	if (!currentPhoto) {
		return false
	}

	try {
		await photoExportingSession.withRenderer(
			session,
			async () => {
				getEditor().renderer.cleanup()
				await loadPhotoIntoEditorForExport(
					currentPhoto,
					options?.allowCanceled ? undefined : session.signal,
					{
						photoId: currentPhoto.id,
						skipHistogram: false,
						skipRenderOnLoad: false,
					},
				)
			},
			{ allowCanceled: options?.allowCanceled },
		)
		return true
	} catch (error) {
		if (session.isCanceled && !options?.allowCanceled) {
			return false
		}
		if (isAbortError(error)) {
			console.warn("[Export] current photo reload was canceled", error)
			return false
		}

		console.warn("[Export] failed to reload current photo after export", error)
		Sentry.captureException(error, { tags: { step: "exportPostReload" } })
		return false
	}
}

export function consumeExportCreditForPhotos(
	photos: Map<PhotoRecord, AdjustmentsHistoryRecord | undefined>,
	projectId: string | undefined,
	options?: {
		onFailure?: (failureMessage: string) => void
		retryWhenPageBecomesVisible?: boolean
		skipCreditConsumption?: boolean
	},
): Promise<void> {
	if (photos.size === 0 || options?.skipCreditConsumption) {
		return Promise.resolve()
	}

	const events: { idempotencyKey: string; metadata: CreditsConsumptionMetadata }[] = []
	for (const [photo, historyRecord] of photos.entries()) {
		const lastHistoryAction = historyRecord
			? HistoryRecordAction[historyRecord.action]
			: "unknown"
		const hasColorMatchApplied = historyRecord
			? !!historyRecord.adjustments?.colorMatchImageId
			: false
		const metadata: CreditsConsumptionMetadata = {
			projectId: projectId ?? "unknown",
			lastHistoryMessage: historyRecord?.message ?? "unknown",
			lastHistoryAction,
			isReferencePhoto: photo.kind === "reference",
			isLockedPhoto: photo.kind === "locked",
			hasColorMatchApplied,
		}
		events.push(buildExportCreditConsumptionEvent(photo.id, metadata))
	}

	if (events.length === 0) {
		return Promise.resolve()
	}

	const retryWhenPageBecomesVisible = options?.retryWhenPageBecomesVisible ?? false

	return consumeExportCredits(events, {
		retryWhenPageBecomesVisible,
	}).catch((err) => {
		const failureMessage = getExportCreditConsumptionFailureMessage(err)
		options?.onFailure?.(failureMessage)
	})
}

function isTIFFExport(
	options: ExportImageOptions,
): options is ExportImageOptions & { type: "tif" } {
	return options.type === "tif"
}

function isPSDExport(options: ExportImageOptions): options is ExportImageOptions & { type: "psd" } {
	return options.type === "psd"
}

function isRGBPlanarArenaExport(options: ExportImageOptions) {
	return isTIFFExport(options) || isPSDExport(options)
}

function getEffectiveRGBPlanarExportBitDepth(
	editor: ReturnType<typeof getEditor>,
	options: ExportImageOptions,
): 8 | 16 {
	return options.bitDepth === 16 && editor.backend.unorm16supported ? 16 : 8
}

function getRGBPlanarExportBufferByteLengths(
	options: ExportImageOptions,
	dimensions: { width: number; height: number; bitDepth: 8 | 16 },
) {
	if (isPSDExport(options)) {
		return getPSDBufferByteLengths(dimensions)
	}

	return getTIFFBufferByteLengths(dimensions)
}

async function batchExportPhotos(props: {
	session: PhotoExportingSession
	performance: ProcessingPerformance
	photos: PhotoRecord[]
	options: ExportImageOptions
	filenames: Record<string, string>
	onCreditConsumptionFailure?: (failureMessage: string) => void
	onHistoryRecordLoaded?: (photo: PhotoRecord, historyRecord?: AdjustmentsHistoryRecord) => void
	errorHandler: (photoId: string, filename: string, error: unknown) => void
	successHandler: (filename: string) => void
}): Promise<{ queue: PQueue; encodeQueue: PQueue; fileQueue: PQueue } | undefined> {
	const {
		session,
		performance,
		photos,
		options,
		filenames,
		onCreditConsumptionFailure,
		onHistoryRecordLoaded,
		errorHandler,
		successHandler,
	} = props
	if (!exportDirectory) return
	const directory = exportDirectory
	const photosWithFilenames = photos.filter((photo) => !!filenames[photo.id])
	performance.start()

	const rgbPlanarPreflightFailures = await reserveRGBPlanarExportBuffersForBatch(
		session,
		photosWithFilenames,
		options,
	)
	const photosReadyForExport = photosWithFilenames.filter((photo) => {
		if (!rgbPlanarPreflightFailures.has(photo.id)) {
			return true
		}

		const preflightError = rgbPlanarPreflightFailures.get(photo.id)
		performance.failed()
		handlePhotoExportError({
			error: preflightError,
			photoId: photo.id,
			flow: "export",
			onStorageError: () => undefined,
		})
		store.set(photosFailedAtom, (prev) => [...prev, filenames[photo.id] ?? photo.fileName])
		return false
	})

	const activeProject = store.get(projectAtom)
	const shouldSkipCreditConsumption =
		activeProject?.isSampleProject === true &&
		(!options.projectId || activeProject.id === options.projectId)

	const queue = new PQueue({ concurrency: 1, autoStart: false })
	const encodeQueue = new PQueue({ concurrency: 1, autoStart: true })
	const fileQueue = new PQueue({ concurrency: 1, autoStart: true })
	const shouldPrefetchImport = shouldPrefetchPhotoOutputImport(options)
	const importLookahead = session.trackLease(
		new PhotoOutputLookahead(photosReadyForExport, (photo) =>
			preparePhotoForExport(photo, session.signal),
		),
	)

	const removeFailedExportFile = async (name: string) => {
		try {
			await directory.removeEntry(name)
		} catch (cleanupError) {
			console.warn("[Export] failed to remove incomplete file", name, cleanupError)
		}
	}

	const encodeOutputTask = async (
		photo: PhotoRecord,
		imageData: PixelData,
		name: string,
		adjustments: Adjustments,
		metadata?: PhotoMetadataRecord,
		historyRecord?: AdjustmentsHistoryRecord,
		yuvBufferLease?: JpegYUVBufferLease,
		rgbPlanarBufferLease?: RGBPlanarDataBufferLease,
	) => {
		startTrackingPerformanceValue(photo.id, "exportEncodeOutput")
		try {
			session.throwIfCanceled()
			const imageInfo = getImageInfo(photo.id)
			const xmpData = getEmbeddableAdjustments(adjustments, imageInfo)
			if (photo.rating != null && photo.rating > 0) {
				xmpData["xmp:Rating"] = photo.rating
			}
			if (isRealColorTag(photo.colorTag)) {
				xmpData["xmp:Label"] = photo.colorTag
			}
			let encodedImageData: Blob | EncodedTIFFData | EncodedPSDData
			if (isTIFFExport(options)) {
				if (!rgbPlanarBufferLease || !("encodeTIFFData" in rgbPlanarBufferLease)) {
					throw new Error("TIFF export requires an acquired RGB data buffer")
				}
				encodedImageData = await rgbPlanarBufferLease.encodeTIFFData(imageData, {
					ppi: options.ppi,
				})
			} else if (isPSDExport(options)) {
				if (!rgbPlanarBufferLease || !("encodePSDData" in rgbPlanarBufferLease)) {
					throw new Error("PSD export requires an acquired RGB data buffer")
				}
				encodedImageData = await rgbPlanarBufferLease.encodePSDData(imageData, {
					ppi: options.ppi,
				})
			} else {
				encodedImageData = await encodeImageData(imageData, {
					...options,
					metadata,
					jpegEncoder: yuvBufferLease ?? JpegEncoder,
					xmpData,
				})
			}
			if (isValidatedExportType(options.type)) {
				validateEncodedExportSizeAndReport({
					format: options.type,
					encoded: encodedImageData,
					width: imageData.width,
					height: imageData.height,
					quality: options.quality,
					iso: metadata?.iso,
					bitDepth: imageData.data instanceof Uint16Array ? 16 : 8,
					photoId: metadata?.photoId,
					colorSpace: imageData.colorSpace,
					sourceFileSize: metadata?.size,
					sourceFileExtension: metadata?.extension,
				})
			}

			if (encodedImageData) {
				await fileQueue.add(async () => {
					session.throwIfCanceled()
					let writable: FileSystemWritableFileStream | undefined
					try {
						const handle = await directory.getFileHandle(name, {
							create: true,
						})

						writable = await handle.createWritable()
						if ("writeTo" in encodedImageData) {
							await encodedImageData.writeTo(writable, { signal: session.signal })
						} else {
							await writable.write(encodedImageData)
						}
						await writable.close()
						writable = undefined
						performance.processed(photo.fileName)
					} catch (error) {
						if (writable) {
							try {
								await writable.abort()
							} catch {
								// ignore secondary abort errors and surface the original write failure
							}
						}
						await removeFailedExportFile(name)
						throw error
					}
				})

				session.throwIfCanceled()
				successHandler(name)
			} else {
				throw new Error("No file handle or encode image data")
			}
			void consumeExportCreditForPhotos(
				new Map([[photo, historyRecord]]),
				options.projectId,
				{
					onFailure: onCreditConsumptionFailure,
					skipCreditConsumption: shouldSkipCreditConsumption,
				},
			)
		} catch (e) {
			if (session.isCanceled) {
				return
			}
			if (isAbortError(e)) {
				console.warn("[Export] encode/output task canceled", e)
				return
			}
			console.error("[Export Error - encode and output]", e)
			errorHandler(photo.id, name, e)
		} finally {
			await session.releaseLease(yuvBufferLease)
			await session.releaseLease(rgbPlanarBufferLease)
			commitTrackingPerformanceValue(photo.id, "exportEncodeOutput")
		}
	}

	photosReadyForExport.forEach((photo, photoIndex) => {
		queue
			.add(async () => {
				const name = filenames[photo.id]

				if (!name) {
					// skipping
					throw new Error("No filename found, skipping export")
				}

				const preparedPhoto = await importLookahead.take(photoIndex)
				const exportedTask = photoExportingSession.withRenderer(session, async () => {
					// Drop the previous photo's textures/passes before allocating
					// for the next one - keeps GPU memory flat across the batch.
					getEditor().renderer.cleanup()
					const result = await loadPreparedPhotoIntoEditorForExport(preparedPhoto)
					if (shouldPrefetchImport) {
						importLookahead.prefetch(photoIndex + 1)
					}
					const { adjustments, metadata, historyRecord } = result
					onHistoryRecordLoaded?.(photo, historyRecord)

					const editor = getEditor()
					startTrackingPerformanceValue(photo.id, "exportBytes")
					let imageData: PixelData
					let yuvBufferLease: JpegYUVBufferLease | undefined
					let rgbPlanarBufferLease: RGBPlanarDataBufferLease | undefined
					let didExportImageData = false
					try {
						if (options.type === "jpg" && options.format === "yuv") {
							const [width, height] = getExportImageSize(
								editor.photo.imageState.croppedImageSize,
								options,
							)
							const byteLength = getYUVExportByteLength(width, height)
							yuvBufferLease = session.trackLease(
								await JpegEncoder.acquireYUVBuffer(byteLength),
							)
							session.throwIfCanceled()
							imageData = await exportImageData(editor, {
								...options,
								bufferView: yuvBufferLease.data,
							})
						} else if (isRGBPlanarArenaExport(options)) {
							const [width, height] = getExportImageSize(
								editor.photo.imageState.croppedImageSize,
								options,
							)
							const bitDepth = getEffectiveRGBPlanarExportBitDepth(editor, options)
							const { pixelByteLength, scratchByteLength } =
								getRGBPlanarExportBufferByteLengths(options, {
									width,
									height,
									bitDepth,
								})
							rgbPlanarBufferLease = session.trackLease(
								isPSDExport(options)
									? await PSDEncoder.acquireRGBDataBuffer({
											pixelByteLength,
											bitDepth,
											scratchByteLength,
										})
									: await TiffEncoder.acquireRGBDataBuffer({
											pixelByteLength,
											bitDepth,
											scratchByteLength,
										}),
							)
							session.throwIfCanceled()
							imageData = await exportImageData(editor, {
								...options,
								format: "rgb-planar",
								bufferView: rgbPlanarBufferLease.data,
							})
						} else {
							imageData = await exportImageData(editor, { ...options })
						}
						didExportImageData = true
					} finally {
						commitTrackingPerformanceValue(photo.id, "exportBytes")
						if (!didExportImageData) {
							await session.releaseLease(yuvBufferLease)
							await session.releaseLease(rgbPlanarBufferLease)
						}
					}

					store.set(photosExportedAtom, (prev) => [...prev, photo.fileName])

					const leaseForEncode = yuvBufferLease
					yuvBufferLease = undefined
					const rgbPlanarLeaseForEncode = rgbPlanarBufferLease
					rgbPlanarBufferLease = undefined

					return {
						adjustments,
						historyRecord,
						imageData,
						leaseForEncode,
						rgbPlanarLeaseForEncode,
						metadata,
					}
				})
				const exported = await (async () => {
					try {
						return await exportedTask
					} finally {
						await preparedPhoto.dispose()
					}
				})()

				void encodeQueue
					.add(() =>
						encodeOutputTask(
							photo,
							exported.imageData,
							name,
							exported.adjustments,
							exported.metadata,
							exported.historyRecord,
							exported.leaseForEncode,
							exported.rgbPlanarLeaseForEncode,
						),
					)
					.catch((err) => {
						if (session.isCanceled) {
							return
						}
						if (isAbortError(err)) {
							console.warn("[Export] encode queue task canceled", err)
							return
						}
						console.error("[Export Error - encode queue]", err)
						errorHandler(photo.id, name, err)
					})
			})
			.catch((err) => {
				if (session.isCanceled) {
					return
				}
				if (isAbortError(err)) {
					console.warn("[Export] export task canceled", err)
					return
				}
				console.error("[Export Error - export]", err)
				errorHandler(photo.id, filenames[photo.id] ?? photo.fileName, err)
			})
	})

	return { queue, encodeQueue, fileQueue }
}

async function reserveRGBPlanarExportBuffersForBatch(
	session: PhotoExportingSession,
	photos: PhotoRecord[],
	options: ExportImageOptions,
) {
	const preflightFailures = new Map<string, unknown>()
	if (!isRGBPlanarArenaExport(options) || photos.length === 0) {
		return preflightFailures
	}

	const metadataByPhotoId = store.get(metadataAtom)
	const bitDepth = getEffectiveRGBPlanarExportBitDepth(getEditor(), options)
	let maxPixelByteLength = 0
	let maxScratchByteLength = 0

	for (const photo of photos) {
		const metadata = metadataByPhotoId[photo.id]
		if (!metadata?.width || !metadata?.height) {
			try {
				const [width, height] = await photoExportingSession.withRenderer(
					session,
					async () => {
						getEditor().renderer.cleanup()
						await loadPhotoIntoEditorForExport(photo, session.signal)

						return getExportImageSize(
							getEditor().photo.imageState.croppedImageSize,
							options,
						)
					},
				)
				const { pixelByteLength, scratchByteLength } = getRGBPlanarExportBufferByteLengths(
					options,
					{
						width,
						height,
						bitDepth,
					},
				)
				maxPixelByteLength = Math.max(maxPixelByteLength, pixelByteLength)
				maxScratchByteLength = Math.max(maxScratchByteLength, scratchByteLength)
			} catch (error) {
				if (session.isCanceled || isAbortError(error)) {
					throw error
				}

				console.warn("[Export] failed to load photo during RGB-planar export preflight", {
					photoId: photo.id,
					error,
				})
				preflightFailures.set(photo.id, error)
			}
			continue
		}

		const possibleSourceSizes: [number, number][] = []
		try {
			const historyRecord = await CatalogManager.fetchAdjustmentHistoryRecord(
				photo.id,
				photo.historyIndex ?? 0,
			)
			const adjustments = {
				...getDefaultAdjustments(photo),
				...historyRecord?.adjustments,
			}

			for (const [sourceWidth, sourceHeight] of [
				[metadata.width, metadata.height],
				[metadata.height, metadata.width],
			] as const) {
				const rotated = Math.abs(adjustments.orientation) % 2 === 1
				const imageWidth = rotated ? sourceHeight : sourceWidth
				const imageHeight = rotated ? sourceWidth : sourceHeight
				possibleSourceSizes.push([
					Math.max(10, Math.round(imageWidth * adjustments.crop[2])),
					Math.max(10, Math.round(imageHeight * adjustments.crop[3])),
				])
			}
		} catch (error) {
			if (session.isCanceled || isAbortError(error)) {
				throw error
			}

			console.warn(
				"[Export] failed to fetch adjustment history during RGB-planar export preflight",
				{
					photoId: photo.id,
					historyIndex: photo.historyIndex ?? 0,
					error,
				},
			)
			possibleSourceSizes.push(
				[metadata.width, metadata.height],
				[metadata.height, metadata.width],
			)
		}

		for (const sourceSize of possibleSourceSizes) {
			const [width, height] = getExportImageSize(sourceSize, options)
			const { pixelByteLength, scratchByteLength } = getRGBPlanarExportBufferByteLengths(
				options,
				{
					width,
					height,
					bitDepth,
				},
			)
			maxPixelByteLength = Math.max(maxPixelByteLength, pixelByteLength)
			maxScratchByteLength = Math.max(maxScratchByteLength, scratchByteLength)
		}
	}

	const reservedPixelByteLength = Math.ceil(
		maxPixelByteLength * RGB_PLANAR_EXPORT_BUFFER_HEADROOM,
	)
	const reservedScratchByteLength = Math.ceil(
		maxScratchByteLength * RGB_PLANAR_EXPORT_BUFFER_HEADROOM,
	)

	const encoder = isPSDExport(options) ? PSDEncoder : TiffEncoder
	encoder.reserveBuffers({
		pixelByteLength: reservedPixelByteLength,
		scratchByteLength: reservedScratchByteLength,
	})

	return preflightFailures
}

export function getExportedFilename(options: {
	photo: PhotoRecord
	filenameType: FilenameType
	fileExtension: string
	filenamePrefix?: string
	number?: number
	trimSpaces?: boolean
}) {
	const { photo, filenameType, fileExtension, filenamePrefix, number, trimSpaces } = options
	const metadata = store.get(metadataAtom)

	let name = ""
	const originalName = photo.fileName.split(".").slice(0, -1).join(".")

	switch (filenameType) {
		case "original":
			name = originalName
			break
		case "custom-sequence":
			name = `${filenamePrefix}${number?.toString().padStart(4, "0")}`
			break
		case "custom-originalNumber": {
			const originalNumber = originalName.match(/\d+/g)?.at(-1)
			if (!originalNumber) {
				return null
			}
			name = `${filenamePrefix}${originalNumber?.toString()}`
			break
		}
		case "date": {
			const fileMetadata = metadata[photo.id]
			const rawDate = fileMetadata?.dateCreated ?? fileMetadata?.dateModified ?? 0

			try {
				const date = new Date(rawDate)
				name = `${formatISODate(date)}-${originalName}`
			} catch (e) {
				Sentry.captureMessage("Exporting with invalid date, falling back to originalName", {
					extra: { rawDate },
				})
				name = originalName
			}
			break
		}
	}

	if (trimSpaces) {
		name = name.replace(/\s+/g, "")
	}

	name = name + "." + fileExtension

	return name
}

function getNewFilenameWithExtension(name: string, countIndex: number) {
	return insertWordInFilename(name, `-${countIndex}`)
}
